import React, { useEffect, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import rawr from "src/common/lib/rawr";
import { Days, ScheduleDay } from "src/common/types";
import { ZonedDateTime, ZoneId } from "@js-joda/core";
import { useRootSelector } from "src/index";
import GateOpenPerSchedule from "src/components/GateOpenPerSchedule";

type Props = {
  children?: React.ReactNode;
};

const SCHEDULE_INTERVAL = 60000; // 1 minute
const RAWR_DELAY_BETWEEN_CALLS = 2000; // 2 seconds

const scheduleDayNames: Days = {
  sun: "SUNDAY",
  mon: "MONDAY",
  tue: "TUESDAY",
  wed: "WEDNESDAY",
  thu: "THURSDAY",
  fri: "FRIDAY",
  sat: "SATURDAY",
};

const shouldGateBeOpenAccordingToSchedule = (
  timezoneId: string,
  scheduleDays: ScheduleDay[]
) => {
  const nowInPropertyTimezone = ZonedDateTime.now(ZoneId.of(timezoneId));

  const scheduleForToday = scheduleDays.find(
    (scheduleDay: ScheduleDay) =>
      scheduleDayNames[scheduleDay.day_of_week] ===
      nowInPropertyTimezone.dayOfWeek().name()
  );

  if (!scheduleForToday) {
    return false;
  }

  const [openHour, openMinute] = scheduleForToday.open_time.split(":");
  const [closeHour, closeMinute] = scheduleForToday.close_time.split(":");
  const scheduleOpen = ZonedDateTime.now(ZoneId.of(timezoneId))
    .withHour(parseInt(openHour, 10))
    .withMinute(parseInt(openMinute, 10))
    .withSecond(0)
    .withNano(0);
  const scheduleClose = ZonedDateTime.now(ZoneId.of(timezoneId))
    .withHour(parseInt(closeHour, 10))
    .withMinute(parseInt(closeMinute, 10))
    .withSecond(0)
    .withNano(0);

  return (
    scheduleOpen.isEqual(nowInPropertyTimezone) ||
    (scheduleOpen.isBefore(nowInPropertyTimezone) &&
      scheduleClose.isAfter(nowInPropertyTimezone))
  );
};

const openOrCloseGatePerSchedule = async (
  device: any,
  flags: any,
  timezoneId: string | undefined
) => {
  if (!device?.schedule_days || !flags.gateSchedules || !timezoneId) {
    return false;
  }

  try {
    const gateStatus = await rawr.methods.gate_status();

    const shouldGateBeOpen =
      device.is_forced_open ||
      shouldGateBeOpenAccordingToSchedule(timezoneId, device.schedule_days);

    if (shouldGateBeOpen) {
      if (gateStatus === "open") {
        return true;
      } else {
        await new Promise((resolve) =>
          setTimeout(resolve, RAWR_DELAY_BETWEEN_CALLS)
        );
        const gateOpenResponse = await rawr.methods.gate_open();
        if (gateOpenResponse === 1) {
          console.log("rawr : gate opened per schedule");
          return true;
        } else {
          console.log("rawr : ERROR opening gate");
        }
      }
    } else {
      if (gateStatus === "closed") {
        return false;
      } else {
        await new Promise((resolve) =>
          setTimeout(resolve, RAWR_DELAY_BETWEEN_CALLS)
        );
        const gateCloseResponse = await rawr.methods.gate_close();
        if (gateCloseResponse === 1) {
          console.log("rawr : gate closed per schedule");
          return false;
        } else {
          console.log("rawr : ERROR closing gate");
        }
      }
    }
  } catch (e) {
    console.log("rawr: ERROR", e);
    return false;
  }
  return false;
};

export const GateScheduleWall = ({ children }: Props) => {
  const [isGateOpenPerSchedule, setGateOpenPerSchedule] = useState(false);
  const flags = useFlags();
  const property = useRootSelector((state) => state.property.meta);
  const device = useRootSelector((state) => state.property.device);
  const timezoneId = property?.timezone_id;

  useEffect(() => {
    const updateGateStatus = async () => {
      const isGateOpen = await openOrCloseGatePerSchedule(
        device,
        flags,
        timezoneId
      );
      setGateOpenPerSchedule(isGateOpen);
    };

    // Once on mount and when the property is refreshed every minute, causing the device info to be refreshed
    updateGateStatus();

    // If the property could not be refreshed, it will still try to update the gate status every minute using this interval
    const intervalId = setInterval(() => {
      updateGateStatus();
    }, SCHEDULE_INTERVAL);

    return () => clearInterval(intervalId);
  }, [device, flags, timezoneId]);

  if (isGateOpenPerSchedule) {
    return <GateOpenPerSchedule />;
  }

  return <>{children}</>;
};
